import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  portalType: null, // Current portal type
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  LOAD_USER_START: 'LOAD_USER_START',
  LOAD_USER_SUCCESS: 'LOAD_USER_SUCCESS',
  LOAD_USER_FAILURE: 'LOAD_USER_FAILURE',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_PORTAL_TYPE: 'SET_PORTAL_TYPE',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.LOAD_USER_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      console.log('🔄 AUTH_ACTIONS.LOGIN_SUCCESS - Setting isAuthenticated to true');
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOAD_USER_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.LOAD_USER_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.SET_PORTAL_TYPE:
      return {
        ...state,
        portalType: action.payload.portalType,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children, portalType }) => {
  const [state, dispatch] = useReducer(authReducer, {
    ...initialState,
    portalType,
  });

  // Load user from token
  const loadUser = async () => {
    console.log('🔍 loadUser called - checking for existing token...');
    const token = Cookies.get('token') || localStorage.getItem('token');

    if (!token) {
      console.log('❌ No token found, setting isLoading to false');
      dispatch({ type: AUTH_ACTIONS.LOAD_USER_FAILURE, payload: { error: 'No token found' } });
      return;
    }

    console.log('✅ Token found, attempting to load user...');

    try {
      dispatch({ type: AUTH_ACTIONS.LOAD_USER_START });
      
      const response = await authAPI.getMe();
      
      if (response.data.success) {
        // Check if user has access to current portal
        if (portalType && !response.data.user.portalAccess?.includes(portalType)) {
          throw new Error(`Access denied to ${portalType} portal`);
        }

        dispatch({
          type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
          payload: { user: response.data.user }
        });
        
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } else {
        throw new Error(response.data.message || 'Failed to load user');
      }
    } catch (error) {
      console.error('Load user error:', error);
      
      // Clear invalid token
      Cookies.remove('token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      dispatch({
        type: AUTH_ACTIONS.LOAD_USER_FAILURE,
        payload: { error: error.response?.data?.message || error.message }
      });
    }
  };

  // Login function
  const login = async (credentials) => {
    try {
      console.log('🚀 Starting login process...');
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });
      
      // Add portal type to credentials
      const loginData = {
        ...credentials,
        portalType: portalType || credentials.portalType
      };
      
      const response = await authAPI.login(loginData);
      console.log('📡 Login API response received:', response.data.success);
      console.log('📡 Full API response:', response.data);

      if (response.data.success) {
        const { user, token } = response.data;
        console.log('✅ Login successful for user:', user?.name || 'Unknown');
        console.log('👤 User object:', user);
        console.log('🔑 Token:', token ? 'Present' : 'Missing');
        
        // Check portal access
        if (portalType && !user.portalAccess?.includes(portalType)) {
          throw new Error(`Access denied to ${portalType} portal`);
        }
        
        // Store token and user
        Cookies.set('token', token, { expires: 7, secure: true, sameSite: 'strict' });
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Dispatch the login success action
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token }
        });

        toast.success('Login successful!');
        console.log('🎉 Authentication state updated - isAuthenticated should now be true');

        return { success: true, user };
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and cookies
      Cookies.remove('token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      
      toast.success('Logged out successfully');
    }
  };

  // Clear error - FIXED: Use useCallback to prevent infinite loops
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []); // Empty dependency array - function never changes

  // Check if user has specific role
  const hasRole = (roleName) => {
    return state.user?.roles?.some(role => role.name === roleName) || false;
  };

  // Check if user has portal access
  const hasPortalAccess = (portalType) => {
    return state.user?.portalAccess?.includes(portalType) || false;
  };

  // Check if user has specific permission
  const hasPermission = (module, feature, action) => {
    if (!state.user?.roles) return false;
    
    return state.user.roles.some(role => 
      role.permissions?.some(permission => 
        permission.module === module &&
        permission.feature === feature &&
        permission.actions?.includes(action)
      )
    );
  };

  // Load user on app start
  useEffect(() => {
    console.log('🚀 AuthProvider mounted, calling loadUser...');
    loadUser();
  }, []);

  // Set portal type
  useEffect(() => {
    if (portalType) {
      dispatch({
        type: AUTH_ACTIONS.SET_PORTAL_TYPE,
        payload: { portalType }
      });
    }
  }, [portalType]);

  const value = {
    ...state,
    login,
    logout,
    loadUser,
    clearError,
    hasRole,
    hasPortalAccess,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
